import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';

type ComponentModule = { default: React.ComponentType<unknown> };
type ImportFunction = () => Promise<ComponentModule>;

interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  onError?: (error: Error, attempt: number) => void;
}

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 1000;

/**
 * Extracts chunk ID and type from error message
 */
function parseChunkError(error: Error): { chunkId: string | null; chunkType: 'css' | 'js' | 'unknown' } {
  const message = error.message;

  // Match patterns like "Loading CSS chunk 34640 failed" or "Loading chunk 123 failed"
  const cssMatch = message.match(/loading css chunk (\d+)/i);
  if (cssMatch) {
    return { chunkId: cssMatch[1], chunkType: 'css' };
  }

  const jsMatch = message.match(/loading chunk (\d+)/i);
  if (jsMatch) {
    return { chunkId: jsMatch[1], chunkType: 'js' };
  }

  return { chunkId: null, chunkType: 'unknown' };
}

/**
 * Tracks retry metrics to Sentry
 */
function trackRetryMetrics(
  event: 'retry_attempt' | 'retry_success' | 'retry_exhausted',
  details: {
    attempt: number;
    maxRetries: number;
    error?: Error;
    chunkId?: string | null;
    chunkType?: 'css' | 'js' | 'unknown';
  }
): void {
  try {
    Sentry.addBreadcrumb({
      category: 'chunk-loading',
      message: `Chunk load ${event}`,
      level: event === 'retry_success' ? 'info' : 'warning',
      data: {
        attempt: details.attempt,
        maxRetries: details.maxRetries,
        chunkId: details.chunkId,
        chunkType: details.chunkType,
        route: window.location.pathname,
      },
    });

    // For successful retries, log a custom event to track recovery rate
    if (event === 'retry_success' && details.attempt > 1) {
      Sentry.captureMessage(`Chunk load recovered after ${details.attempt} attempts`, {
        level: 'info',
        tags: {
          event_type: 'chunk_retry_success',
          chunk_type: details.chunkType || 'unknown',
          attempts_needed: String(details.attempt),
        },
        extra: {
          chunkId: details.chunkId,
          route: window.location.pathname,
        },
      });
    }
  } catch (e) {
    // Silently fail if Sentry is not initialized
    console.warn('Could not track metrics:', e);
  }
}

/**
 * Global version counter for lazy components.
 * When incremented, all lazyWithRetry components will create fresh lazy instances.
 */
let lazyVersion = 0;

/**
 * Listeners that get notified when version changes
 */
const versionListeners = new Set<() => void>();

/**
 * Subscribe to version changes
 */
export function subscribeToLazyVersion(listener: () => void): () => void {
  versionListeners.add(listener);
  return () => versionListeners.delete(listener);
}

/**
 * Get current lazy version
 */
export function getLazyVersion(): number {
  return lazyVersion;
}

/**
 * Increment the lazy version to force all lazy components to recreate their instances.
 * Call this before retrying after a chunk load error.
 */
export function refreshLazyComponents(): void {
  lazyVersion++;
  versionListeners.forEach((listener) => listener());
}

/**
 * Clears failed chunks from Rspack/Webpack runtime cache.
 * This allows the bundler to re-attempt loading failed chunks.
 * Only removes CSS links that actually failed to load (sheet is null).
 */
export function clearFailedChunks(): void {
  try {
    // 1. Remove only FAILED CSS link tags from DOM (not successfully loaded ones)
    // A link element that successfully loaded will have sheet !== null
    const links = document.querySelectorAll('link[rel="stylesheet"]');
    links.forEach((link) => {
      const linkElement = link as HTMLLinkElement;
      const href = linkElement.href || '';

      // Only remove async CSS chunks that failed to load
      if ((href.includes('/static/css/async/') || href.includes('chunk')) && !linkElement.sheet) {
        link.remove();
      }
    });

    // 2. Clear Rspack/Webpack runtime chunk caches
    // Access the webpack require function
    const globalObj = window as unknown as Record<string, unknown>;

    // Try to access __webpack_require__ (standard webpack/rspack)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webpackRequire = (globalObj as any).__webpack_require__;

    if (webpackRequire) {
      // Clear the module cache for failed modules
      if (webpackRequire.c) {
        const moduleCache = webpackRequire.c as Record<string, unknown>;
        Object.keys(moduleCache).forEach((key) => {
          // Remove modules that might be related to failed chunks
          // We're conservative here - only remove if it looks like a chunk module
          const module = moduleCache[key] as { l?: boolean; error?: unknown } | undefined;
          if (module && module.error) {
            delete moduleCache[key];
          }
        });
      }

      // Try to access chunk caches via different patterns
      const chunkCacheKeys = ['installedChunks', 'installedCssChunks', 'O'];
      chunkCacheKeys.forEach((key) => {
        if (webpackRequire[key] && typeof webpackRequire[key] === 'object') {
          const cache = webpackRequire[key] as Record<string | number, unknown>;
          Object.keys(cache).forEach((chunkId) => {
            const status = cache[chunkId];
            // 0 = installed, undefined = not loaded, Array/Promise = loading, null = prefetched
            // We want to clear anything that's not successfully installed (0)
            if (status !== 0 && status !== undefined) {
              delete cache[chunkId];
            }
          });
        }
      });
    }

    // 3. Also check for global chunk caches (some bundlers expose these)
    ['installedChunks', 'installedCssChunks', 'webpackChunk', '__webpack_chunk_cache__'].forEach((key) => {
      if (globalObj[key] && typeof globalObj[key] === 'object') {
        const cache = globalObj[key] as Record<string | number, unknown>;
        Object.keys(cache).forEach((chunkId) => {
          const status = cache[chunkId];
          if (status !== 0 && status !== undefined) {
            delete cache[chunkId];
          }
        });
      }
    });

  } catch (e) {
    console.warn('Could not clear chunk cache:', e);
  }
}

/**
 * Checks if an error is a chunk load error (CSS or JS)
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  return (
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * Delay utility with exponential backoff
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retries an import function with exponential backoff.
 * Tracks retry metrics to Sentry for monitoring success/failure rates.
 */
async function retryImport(
  importFn: ImportFunction,
  options: RetryOptions = {}
): Promise<ComponentModule> {
  const { maxRetries = DEFAULT_MAX_RETRIES, baseDelayMs = DEFAULT_BASE_DELAY_MS, onError } = options;

  let lastError: Error | null = null;
  let chunkInfo: { chunkId: string | null; chunkType: 'css' | 'js' | 'unknown' } = {
    chunkId: null,
    chunkType: 'unknown',
  };

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Small delay before retry with exponential backoff
      if (attempt > 1) {
        const delayMs = baseDelayMs * Math.pow(2, attempt - 2); // 1s, 2s, 4s...
        await delay(delayMs);
      }

      const module = await importFn();

      // Track successful retry if it took more than one attempt
      if (attempt > 1) {
        trackRetryMetrics('retry_success', {
          attempt,
          maxRetries,
          chunkId: chunkInfo.chunkId,
          chunkType: chunkInfo.chunkType,
        });
      }

      return module;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      chunkInfo = parseChunkError(lastError);

      if (onError) {
        onError(lastError, attempt);
      }

      // Only retry for chunk load errors
      if (!isChunkLoadError(lastError)) {
        throw lastError;
      }

      // Track retry attempt
      trackRetryMetrics('retry_attempt', {
        attempt,
        maxRetries,
        error: lastError,
        chunkId: chunkInfo.chunkId,
        chunkType: chunkInfo.chunkType,
      });

      // If this was the last attempt, track exhaustion and throw
      if (attempt === maxRetries) {
        trackRetryMetrics('retry_exhausted', {
          attempt,
          maxRetries,
          error: lastError,
          chunkId: chunkInfo.chunkId,
          chunkType: chunkInfo.chunkType,
        });
        throw lastError;
      }
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Import failed');
}

/**
 * Enhanced React.lazy with retry logic for chunk loading failures.
 * Returns a component that creates fresh lazy instances when refreshLazyComponents() is called.
 *
 * @example
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 *
 * @example with custom options
 * const MyComponent = lazyWithRetry(
 *   () => import('./MyComponent'),
 *   { maxRetries: 5, baseDelayMs: 500 }
 * );
 */
export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): React.ComponentType<React.ComponentProps<T>> {
  // Cache of lazy components by version
  const lazyCache = new Map<number, React.LazyExoticComponent<T>>();

  // Function to get or create lazy component for a version
  const getLazyForVersion = (version: number): React.LazyExoticComponent<T> => {
    if (!lazyCache.has(version)) {
      // Create a fresh lazy component for this version
      const lazyComponent = React.lazy(
        () => retryImport(importFn as ImportFunction, options) as Promise<{ default: T }>
      );
      lazyCache.set(version, lazyComponent);

      // Clean up old versions to prevent memory leaks (keep last 2)
      if (lazyCache.size > 2) {
        const oldestVersion = Math.min(...Array.from(lazyCache.keys()));
        lazyCache.delete(oldestVersion);
      }
    }
    return lazyCache.get(version)!;
  };

  // Create a wrapper component that subscribes to version changes
  function LazyWithRetryWrapper(props: Record<string, unknown>): JSX.Element {
    const [version, setVersion] = useState(getLazyVersion);

    useEffect(() => {
      // Subscribe to version changes
      const unsubscribe = subscribeToLazyVersion(() => {
        setVersion(getLazyVersion());
      });
      return unsubscribe;
    }, []);

    const LazyComponent = getLazyForVersion(version);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return React.createElement(LazyComponent as React.ComponentType<any>, props);
  }

  // Set display name for debugging
  LazyWithRetryWrapper.displayName = `LazyWithRetry(${importFn.toString().slice(0, 50)}...)`;

  return LazyWithRetryWrapper;
}

export default lazyWithRetry;